import React, { useState } from 'react';
import { Play, Pause, Save, RotateCcw, LayoutGrid, ScrollText } from 'lucide-react';

interface GameHeaderProps {
  gameSpeed: number;
  setGameSpeed: (speed: number) => void;
  isPaused: boolean;
  setIsPaused: (paused: boolean) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
  onOpenSaveModal: () => void;
  onResetGame: () => void;
  onOpenManagement: () => void;
  onOpenEventLog: () => void;
  eventLogUnread: number;
  hasUnlockPulse?: boolean;
}

const GameHeader: React.FC<GameHeaderProps> = ({
  gameSpeed,
  setGameSpeed,
  isPaused,
  setIsPaused,
  onOpenSaveModal,
  onResetGame,
  onOpenManagement,
  onOpenEventLog,
  eventLogUnread,
  hasUnlockPulse = false,
}) => {
  const [confirmReset, setConfirmReset] = useState(false);

  const handleResetClick = () => {
    if (confirmReset) {
      onResetGame();
      setConfirmReset(false);
    } else {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 4000);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Play / Pause */}
      <button
        className={`px-3 py-1 ${isPaused ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} rounded text-sm font-semibold flex items-center gap-1`}
        onClick={() => setIsPaused(!isPaused)}
      >
        {isPaused ? <Play size={16} /> : <Pause size={16} />}
        {isPaused ? 'Play' : 'Pause'}
      </button>

      {/* Speed selector */}
      <div className="flex bg-gray-700 rounded overflow-hidden">
        {[1, 2, 5, 10, 20, 50].map(s => (
          <button
            key={s}
            className={`px-3 py-1 text-sm font-semibold ${gameSpeed === s ? 'bg-blue-600' : 'hover:bg-gray-600'}`}
            onClick={() => setGameSpeed(s)}
          >
            {s}x
          </button>
        ))}
      </div>

      {/* Save / Load modal */}
      <button
        className="px-3 py-1 bg-green-700 hover:bg-green-600 rounded text-sm font-semibold flex items-center gap-1"
        onClick={onOpenSaveModal}
        title="Open save/load menu"
      >
        <Save size={16} />
        Saves
      </button>

      {/* Reset */}
      <button
        className={`px-3 py-1 rounded text-sm font-semibold flex items-center gap-1 ${confirmReset ? 'bg-red-600 hover:bg-red-500 animate-pulse' : 'bg-gray-600 hover:bg-gray-500'}`}
        onClick={handleResetClick}
        title={confirmReset ? 'Click again to confirm — all progress lost!' : 'Reset game'}
      >
        <RotateCcw size={16} />
        {confirmReset ? 'Confirm?' : 'Reset'}
      </button>

      {/* Management panel toggle */}
      <button
        className={`relative px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-semibold flex items-center gap-1 ${hasUnlockPulse ? 'ring-2 ring-emerald-400 animate-pulse' : ''}`}
        onClick={onOpenManagement}
        title={hasUnlockPulse ? 'New tools unlocked — open to see them' : 'Open management panel'}
      >
        <LayoutGrid size={16} />
        Manage
        {hasUnlockPulse && (
          <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-emerald-400 border border-gray-900" />
        )}
      </button>

      {/* Event log */}
      <button
        className="relative px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm font-semibold flex items-center gap-1"
        onClick={onOpenEventLog}
        title="Open event log"
      >
        <ScrollText size={16} />
        Events
        {eventLogUnread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {eventLogUnread > 99 ? '99+' : eventLogUnread}
          </span>
        )}
      </button>
    </div>
  );
};

export default GameHeader;
